/**
 * `ListAnswer` — what a list screen is showing WHERE THE ROWS GO, as one value.
 *
 * =============================================================================
 * WHY THIS EXISTS: FIVE ROUNDS OF FIXES, ONE SHAPE OF DEFECT
 * =============================================================================
 * `QuotesListPage` and `SalesOrdersListPage` have now shipped the same class of
 * untrue sentence five times. Every instance had the identical structure — a node
 * that says something about "the rows below" was DECIDED BY SOMETHING OTHER THAN
 * the branch that decides what is below it:
 *
 *  · the counts-failure Alert said "The list below is still accurate." while the
 *    region beneath it said "These rows are the previous result";
 *  · the pager printed "1–50 of 60" with a live Previous/Next under an error card
 *    that had replaced every row with "Something went wrong";
 *  · the sales-order banner counted "2 of the orders below are recorded here"
 *    over that same error card, because `list.data` SURVIVES an errored refetch
 *    and `isPlaceholderData` is false in the error state, so the dim did not save
 *    it either.
 *
 * All three were reachable for one reason: the page kept the payload in its own
 * scope — `const shown = list.data ?? null; const rows = shown?.data ?? []` — so
 * ANY node in the file could quote it, whatever the branch below had decided to
 * render. Each previous fix guarded one more node against one more state, and
 * each left the next node unguarded. That is why there was always a next one.
 *
 * =============================================================================
 * THE ARRANGEMENT: DELETE THE SHARED SCOPE, DO NOT GUARD IT
 * =============================================================================
 * `listAnswer()` collapses the query into exactly one of five shapes, and the
 * payload fields live on the VARIANTS rather than on the union:
 *
 *   loading  — nothing to quote, and nothing to quote it FROM
 *   failed   — carries the error and NO payload, so the `list.data` that survives
 *              an errored refetch is not reachable from this value at all
 *   rows     — the only variant with `rows`, and the only one that can be `behind`
 *   pastEnd  — a payload whose page is past its end: `shown`, and no rows
 *   empty    — a genuinely empty result set: `shown`, and no rows
 *
 * WHAT THIS BUYS OVER THREE MORE GUARDS, STATED AT ITS ACTUAL STRENGTH.
 * `shown`, `rows` and `behind` are not properties of `ListAnswer`; they are
 * properties of particular variants. TypeScript therefore REFUSES to read them
 * until the writer has narrowed `kind` — so a node written THROUGH `answer`
 * cannot forget which state it is describing. That is a real guarantee and it is
 * why the three guards became one arrangement.
 *
 * IT IS NOT A GUARANTEE ABOUT THE FILE, AND AN EARLIER DRAFT OF THIS BLOCK SAID
 * IT WAS. That draft claimed "there is no second copy of that state left in
 * either page to be decided by instead" and "the compiler is the reviewer".
 * Both are overstatements, and a comment claiming a property the code does not
 * have is what makes the next reader stop looking — the failure this whole
 * arrangement exists to end. The page-scope DERIVATIVES were deleted; the
 * ORIGINAL was not. `list` (the whole `UseQueryResult`) is still in scope in
 * both pages, `list.data` is one property access away, and `QuotesListPage`
 * legitimately still reads `list.isFetching` for the pager's busy state. A
 * reviewer proved the gap by inserting a plausible sixth node that prints
 * "N of M quotes on this page" off `list.data`, outside the answer branch:
 * `tsc` accepted it and the suite stayed green.
 *
 * So the honest statement is: THE COMPILER ENFORCES THE RULE ON EVERY NODE THAT
 * GOES THROUGH `answer`, AND ON NO OTHER. Reaching past it to `list.data` still
 * compiles, and is still the bug. If that ever needs to be airtight, the move is
 * to stop passing `list` into these pages at all — hand each page an `answer`
 * plus the two flags it genuinely needs — and that is a bigger change than this
 * one, deliberately not taken here.
 *
 * The two pages consequently hold NO `shown`, NO `rows`, NO `total` and no
 * separate `isPlaceholderData`. They hold `answer`, and the derived boolean
 * `behind` — which is `answer.kind === 'rows' && answer.behind`, false by
 * construction in every state that has no rows, and therefore the one thing that
 * IS safe to read without narrowing.
 *
 * =============================================================================
 * `behind` LIVES ON `rows` AND NOWHERE ELSE, AND THAT IS A FACT NOT A CHOICE
 * =============================================================================
 * V4 established that a rows-less placeholder is a LOAD with nothing to show
 * (`holdingNothing`): holding it pins the reader to a result they have navigated
 * away from, and every branch below would then describe that result. So it is
 * `loading` here — which means `pastEnd` and `empty` cannot be behind anything,
 * and the `empty` card's URL-derived status label is describing the same question
 * the payload answers. The union states that; two loose booleans could not.
 *
 * =============================================================================
 * THE BRANCH ORDER IS THE ORDER BOTH SCREENS ALREADY HAD
 * =============================================================================
 * pending-or-holding-nothing, then error, then the three payload shapes — which
 * is `QuotesListPage`'s chain verbatim (`list.isPending || holdingNothing` →
 * `list.isError` → `total > 0` → `rows.length === 0` → table). No settled render
 * moves; what changes is only which nodes can still speak in the first two.
 */

import type { UseQueryResult } from '@tanstack/react-query';
import type { Paged } from './api';

export type ListAnswer<T> =
  /** The screen has nothing of THIS question to show, and says so. */
  | { kind: 'loading' }
  /**
   * The request was refused. No payload: an errored refetch leaves `list.data`
   * standing, and reading it is exactly how the pager and the Phase-3b banner
   * came to describe rows that are not on screen.
   */
  | { kind: 'failed'; error: unknown }
  /**
   * A payload with rows. `behind` marks the rows as the PREVIOUS question's —
   * true only here, because a rows-less placeholder is `loading` above.
   */
  | { kind: 'rows'; shown: Paged<T>; rows: readonly T[]; behind: boolean }
  /** A payload whose page is past the end of a result set that HAS rows. */
  | { kind: 'pastEnd'; shown: Paged<T> }
  /** A payload for a result set with no rows in it at all. */
  | { kind: 'empty'; shown: Paged<T> };

/**
 * Collapse a paged list query into the one shape the screen is in.
 *
 * Takes the whole `UseQueryResult` rather than its pieces on purpose: the pieces
 * are what the pages used to spread across their own scope, and handing them out
 * again would rebuild the thing this file exists to remove.
 */
export function listAnswer<T>(list: UseQueryResult<Paged<T>>): ListAnswer<T> {
  const shown = list.data ?? null;
  const rows = shown?.data ?? [];
  const behind = list.isPlaceholderData;

  if (list.isPending || (behind && rows.length === 0)) return { kind: 'loading' };
  if (list.isError) return { kind: 'failed', error: list.error };
  /*
   * Unreachable: past the two branches above the query is settled and successful,
   * so `data` is defined. It is spelled out rather than asserted away — a `!` here
   * would be the one place in this file where a payload is taken on trust.
   */
  if (shown === null) return { kind: 'loading' };

  if (rows.length > 0) return { kind: 'rows', shown, rows, behind };
  return shown.total > 0 ? { kind: 'pastEnd', shown } : { kind: 'empty', shown };
}
